import React, { useState, useCallback } from 'react'
import { useMutation } from '@apollo/client'
import { Button, TextArea, Row, useStatusOverlay, Separator } from '@fs/zion-ui'
import { usePermission } from '@fs/zion-permissions'

import { useUser } from '@fs/zion-user'
import { useGroupData } from '../../../../hooks'
import mutations from '../../../../mutations'
import transl from './ReworkFolder.transl'
import useCurrentReworkStatus from './useCurrentReworkStatus'

function showResults(type) {
  if (!type) return { successString: transl.requestSuccess, failedString: transl.requestFailed }
  const leadingString = type === 'cancelRework' ? 'deny' : type
  return { successString: transl[`${leadingString}Success`], failedString: transl[`${leadingString}Failed`] }
}

function mapRequestToAction(request) {
  const map = {
    supervisorRequest: 'Available',
    requestRework: 'Available',
    deny: 'Cancelled',
    cancelReworkRequest: 'Cancelled',
    cancel: 'Cancelled',
    approve: 'Available',
  }

  return map[request]
}

const ReworkButtons = ({ reworkHistory, addReworkItem }) => {
  const {
    group: { id = '' },
  } = useGroupData()
  const showStatusOverlay = useStatusOverlay()
  const user = useUser()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [showComment, setShowComment] = useState(false)
  const [comment, setComment] = useState('')
  const [canApprove] = usePermission('LightyearApproveReworkPermission', 'LightyearContext')
  const [canRequest] = usePermission('LightyearRequestReworkPermission', 'LightyearContext')

  const [isDenyComment, setIsDenyComment] = useState(false)
  const latestItem = reworkHistory?.[reworkHistory.length - 1] || { state: '', action: '' }

  const { resetCurrentReworkStatus: resetStatus, setReworkStatusError } = useCurrentReworkStatus(latestItem)
  const [updateReworkStatus] = useMutation(mutations.updateCameraReworkStatus())

  const postReworkStatus = useCallback(
    async (action) => {
      const { successString, failedString } = showResults(action)
      let success = true
      setLoading(true)

      const resolvedAction = canApprove && action === 'requestRework' ? 'supervisorRequest' : action

      try {
        const { data } = await updateReworkStatus({
          variables: {
            updateParams: { action: resolvedAction, comment, projectId: id },
          },
        })
        if (data?.updateCameraReworkStatus?.code !== '204')
          throw new Error(data?.updateCameraReworkStatus?.message || 'unknown error')
        else if (data?.updateCameraReworkStatus.message === 'Camera Rework already under way') {
          showStatusOverlay({
            message: data?.updateCameraReworkStatus.message,
            type: 'warning',
          })
          setShowComment(false)
          setLoading(false)
          return
        } else
          addReworkItem({
            action: ['supervisorRequest', 'approve'].includes(resolvedAction) ? 'CameraRework' : 'RequestCameraRework',
            date: {
              fromTimestamp: new Date().getTime(),
            },
            state: mapRequestToAction(resolvedAction),
            notes: comment,
            username: user.contactName || '',
          })
        resetStatus(action)
      } catch (err) {
        setError(true)
        success = false
        setReworkStatusError('')
      }

      showStatusOverlay({
        message: success ? successString : failedString,
        dismissable: true,
        type: success ? 'success' : 'error',
      })
      setLoading(false)
      handleBackClick()
    },
    [
      comment,
      id,
      resetStatus,
      setReworkStatusError,
      showStatusOverlay,
      updateReworkStatus,
      addReworkItem,
      user,
      canApprove,
    ]
  )

  const BaseButton = useCallback(
    ({ children, onClick, pad = false }) => {
      return (
        <>
          {pad && <Separator />}
          <Button emphasis="medium" disabled={loading} onClick={onClick}>
            {children}
          </Button>
        </>
      )
    },
    [loading]
  )

  function handleBackClick() {
    setComment('')
    setShowComment(false)
    setIsDenyComment(false)
  }

  function showDenyComment() {
    setComment('')
    setShowComment(true)
    setIsDenyComment(true)
  }

  const hasSomePermission = canApprove || canRequest
  const isAvailableOrInProgress = ['Available', 'In Progress'].includes(latestItem?.state)
  const isRequestAction = latestItem?.action === 'RequestCameraRework'

  const showCancel = hasSomePermission && isAvailableOrInProgress && !isRequestAction
  const showApprove = canApprove && latestItem?.state === 'Available' && isRequestAction && !showComment
  const showRequest = !showCancel && !showApprove && hasSomePermission && !isAvailableOrInProgress && !showComment
  const showCancelRequest = canRequest && !canApprove && isRequestAction && latestItem?.state === 'Available'

  return (
    <>
      {!error && (
        <>
          {showCancelRequest && (
            <BaseButton pad onClick={() => postReworkStatus('cancelReworkRequest')}>
              {transl.cancelReworkRequest()}
            </BaseButton>
          )}
          {showRequest && (
            <BaseButton pad onClick={() => setShowComment(true)}>
              {transl.requestRework()}
            </BaseButton>
          )}
          {showCancel && (
            <BaseButton pad onClick={() => postReworkStatus('cancel')}>
              {transl.cancelRework}
            </BaseButton>
          )}
          {showApprove && (
            <>
              <BaseButton pad onClick={() => postReworkStatus('approve')}>
                {transl.approveRework}
              </BaseButton>
              <BaseButton onClick={showDenyComment}>{transl.denyRework}</BaseButton>
            </>
          )}
          {showComment && (
            <>
              <Separator size="sm" />
              <TextArea
                name="Rework Comment"
                onChange={(e) => setComment(e.target.value)}
                value={comment}
                placeholder={isDenyComment ? transl.denyNote : transl.reworkNote}
              />
              <Row>
                <BaseButton onClick={() => postReworkStatus(isDenyComment ? 'deny' : 'requestRework')}>
                  {transl.submit}
                </BaseButton>
                <BaseButton onClick={handleBackClick}>{transl.cancel}</BaseButton>
              </Row>
            </>
          )}
        </>
      )}
    </>
  )
}

export default ReworkButtons
